import { apiClient } from './client';
import type {
  DashboardSummary,
  CountyMetrics,
  DistrictMetrics,
  OfficerMetrics,
  TrendPoint,
} from '@types';

export const dashboardApi = {
  /**
   * Get dashboard summary metrics.
   * Cached by backend for 30 seconds.
   * Never includes raw citizen data - only aggregated counts.
   */
  async getSummary(): Promise<DashboardSummary> {
    return apiClient.get('/dashboard/summary/');
  },

  /**
   * Get metrics grouped by county.
   * Shows total, registered, unregistered counts and percentage.
   * Cached by backend for 30 seconds.
   */
  async getByCounty(): Promise<CountyMetrics[]> {
    return apiClient.get('/dashboard/by-county/');
  },

  /**
   * Get metrics grouped by district within a county.
   * Cached by backend for 30 seconds.
   */
  async getByDistrict(county?: string): Promise<DistrictMetrics[]> {
    const url = county
      ? `/dashboard/by-district/?county=${encodeURIComponent(county)}`
      : '/dashboard/by-district/';
    return apiClient.get(url);
  },

  /**
   * Registered voters per polling station under the selected county /
   * subcounty / division / location (unregistered citizens have none).
   */
  async getPollingStations(scope: { county?: string; district?: string; division?: string; location?: string }): Promise<{
    county: string; district: string; division: string; location: string;
    polling_station: string; registered_count: number;
  }[]> {
    const params = new URLSearchParams();
    if (scope.county) params.append('county', scope.county);
    if (scope.district) params.append('district', scope.district);
    if (scope.division) params.append('division', scope.division);
    if (scope.location) params.append('location', scope.location);
    return apiClient.get(`/dashboard/polling-stations/?${params.toString()}`);
  },

  /**
   * Get aggregate counts scoped to whatever level of county/district/division/
   * location is selected — backs the filter dropdowns so the KPI tiles reflect
   * the current selection instead of always showing national totals.
   * Cached by backend for 30 seconds, per exact scope.
   */
  async getScopeSummary(scope: { county?: string; district?: string; division?: string; location?: string }): Promise<{
    county: string | null; district: string | null; division: string | null; location: string | null;
    total_count: number; registered_count: number; unregistered_count: number;
    registration_percentage: number; registered_today: number;
    pool_count?: number; converted_count?: number; conversion_percentage?: number;
  }> {
    const params = new URLSearchParams();
    if (scope.county) params.append('county', scope.county);
    if (scope.district) params.append('district', scope.district);
    if (scope.division) params.append('division', scope.division);
    if (scope.location) params.append('location', scope.location);
    const qs = params.toString();
    return apiClient.get(`/dashboard/scope-summary/${qs ? `?${qs}` : ''}`);
  },

  /**
   * Get metrics grouped by officer.
   * Shows registration counts for each officer, including today's count.
   * Cached by backend for 30 seconds.
   */
  async getByOfficer(status?: string): Promise<OfficerMetrics[]> {
    const url = status
      ? `/dashboard/by-officer/?status=${encodeURIComponent(status)}`
      : '/dashboard/by-officer/';
    return apiClient.get(url);
  },

  /**
   * Get registration trends by day.
   * Returns daily registration counts for the specified period.
   * Cached by backend for 5 minutes.
   */
  async getTrends(days?: number, startDate?: string, endDate?: string): Promise<TrendPoint[]> {
    const params = new URLSearchParams();
    if (days) params.append('days', String(days));
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const url = params.toString()
      ? `/dashboard/trends/?${params.toString()}`
      : '/dashboard/trends/';
    return apiClient.get(url);
  },

  /**
   * Invalidate dashboard cache (admin only).
   * Manually trigger cache refresh if needed.
   */
  async invalidateCache(): Promise<void> {
    return apiClient.post('/dashboard/invalidate-cache/');
  },
};
